import * as THREE from "three";

export type Material = "soil" | "ceramic" | "plastic" | "wood" | "metal" | "bread" | "snow";

export const MATERIAL_NAMES: Record<Material, string> = {
  soil: "土",
  ceramic: "陶器",
  plastic: "プラスチック",
  wood: "木",
  metal: "金属",
  bread: "パン",
  snow: "雪",
};

export const FINISH_NAMES = {
  soil: { unglazed: "素焼き" },
  ceramic: { glossy: "つや", matte: "マット" },
  plastic: { glossy: "つや", matte: "マット" },
  wood: { unpainted: "無塗装", varnished: "ニス" },
  metal: { polished: "磨き", tarnished: "くすみ" },
  bread: { toasted: "焼きっぱなし", eggGlaze: "卵つや" },
  snow: { damp: "しっとり", packed: "圧雪" },
} as const;

type AppearanceFor<M extends Material> = {
  [F in keyof typeof FINISH_NAMES[M]]: { material: M; finish: F; color: number };
}[keyof typeof FINISH_NAMES[M]];

/** A requested appearance keeps its material, fixed color, and material-specific finish separate. */
export type Appearance = { [M in Material]: AppearanceFor<M> }[Material];

/** Finish identifiers kept by older saved pieces. */
export type LegacyFinish = "fired" | "snow" | "plastic" | "bread";

export const DEFAULT_APPEARANCES = {
  soil: { material: "soil", finish: "unglazed", color: 0xb98252 },
  ceramic: { material: "ceramic", finish: "glossy", color: 0xd98f66 },
  plastic: { material: "plastic", finish: "glossy", color: 0x3fae5c },
  wood: { material: "wood", finish: "varnished", color: 0xc89868 },
  metal: { material: "metal", finish: "polished", color: 0xc8d1dc },
  bread: { material: "bread", finish: "eggGlaze", color: 0xd0924a },
  snow: { material: "snow", finish: "packed", color: 0xf4f8fc },
} satisfies { [M in Material]: Extract<Appearance, { material: M }> };

/**
 * The materials a piece kneaded freely can be finished as, each with its initial finish and
 * fixed color (opened by the requests cleared, see progression.ts).
 */
export const FREE_APPEARANCES: Appearance[] = [
  DEFAULT_APPEARANCES.ceramic,
  DEFAULT_APPEARANCES.soil,
  DEFAULT_APPEARANCES.bread,
  DEFAULT_APPEARANCES.snow,
  DEFAULT_APPEARANCES.plastic,
  DEFAULT_APPEARANCES.wood,
  DEFAULT_APPEARANCES.metal,
];

const LEGACY_APPEARANCES: Record<LegacyFinish, Appearance> = {
  fired: DEFAULT_APPEARANCES.ceramic,
  snow: DEFAULT_APPEARANCES.snow,
  plastic: DEFAULT_APPEARANCES.plastic,
  bread: DEFAULT_APPEARANCES.bread,
};

export function pieceAppearance(appearance?: Appearance, legacyFinish?: LegacyFinish): Appearance {
  return appearance ?? LEGACY_APPEARANCES[legacyFinish ?? "fired"];
}

export function appearanceLabel(appearance: Appearance): string {
  const finishes = FINISH_NAMES[appearance.material] as Record<string, string>;
  return `${MATERIAL_NAMES[appearance.material]}・${finishes[appearance.finish]}`;
}

export const requiresKiln = (appearance: Appearance) =>
  appearance.material === "soil" || appearance.material === "ceramic" ||
  appearance.material === "bread";

export const shrinksInKiln = (appearance: Appearance) =>
  appearance.material === "soil" || appearance.material === "ceramic";

export const glowsInKiln = (appearance: Appearance) =>
  appearance.material === "soil" || appearance.material === "ceramic";

const surface = (
  color: number,
  roughness: number,
  extra: THREE.MeshPhysicalMaterialParameters = {},
) => new THREE.MeshPhysicalMaterial({ color, roughness, ...extra });

/** Surface response by material and its valid finish; color is supplied by each request. */
export const FINISHES: Record<string, THREE.MeshPhysicalMaterial> = {
  "soil:unglazed": surface(0xb98252, 0.9),
  "ceramic:glossy": surface(0xd98f66, 0.3, {
    sheen: 0.2,
    sheenRoughness: 0.45,
    sheenColor: new THREE.Color(0xffd6bd),
    clearcoat: 1,
    clearcoatRoughness: 0.12,
  }),
  "ceramic:matte": surface(0xd98f66, 0.82, { sheen: 0.18, clearcoat: 0.05 }),
  "plastic:glossy": surface(0x3fae5c, 0.35, { clearcoat: 0.6, clearcoatRoughness: 0.25 }),
  "plastic:matte": surface(0x3fae5c, 0.78, { clearcoat: 0.08 }),
  "wood:unpainted": surface(0xc89868, 0.72),
  "wood:varnished": surface(0xc89868, 0.32, { clearcoat: 0.7, clearcoatRoughness: 0.28 }),
  "metal:polished": surface(0xc8d1dc, 0.2, { metalness: 0.92 }),
  "metal:tarnished": surface(0x8a8178, 0.78, { metalness: 0.72 }),
  "bread:toasted": crust(false),
  "bread:eggGlaze": crust(true),
  "snow:damp": surface(0xf4f8fc, 0.38, {
    sheen: 0.65,
    sheenRoughness: 0.35,
    sheenColor: new THREE.Color(0xd6e6f5),
  }),
  "snow:packed": surface(0xf4f8fc, 0.75, {
    sheen: 0.6,
    sheenRoughness: 0.5,
    sheenColor: new THREE.Color(0xd6e6f5),
  }),
};

const byAppearance = new Map<string, THREE.MeshPhysicalMaterial>();

export function appearanceMaterial(appearance: Appearance): THREE.MeshPhysicalMaterial {
  const key = `${appearance.material}:${appearance.finish}:${appearance.color}`;
  let material = byAppearance.get(key);
  if (!material) {
    const finish = FINISHES[`${appearance.material}:${appearance.finish}`]!;
    material = finish.clone();
    // Three.js Material.clone() copies material values but not onBeforeCompile callbacks.
    // Keep finish-specific shaders such as the bread's baked gradient on each appearance.
    material.onBeforeCompile = finish.onBeforeCompile;
    material.color.setHex(appearance.color);
    byAppearance.set(key, material);
  }
  return material;
}

/** Materials shared by the workshop and shelf; callers must not dispose these. */
export function appearanceMaterials(): THREE.Material[] {
  return [...Object.values(FINISHES), ...byAppearance.values()];
}

/** Bread browns toward the heat and pales underneath, with the egg glaze as a surface finish. */
function crust(eggGlaze: boolean): THREE.MeshPhysicalMaterial {
  const material = new THREE.MeshPhysicalMaterial({
    color: 0xd0924a,
    roughness: eggGlaze ? 0.55 : 0.72,
    clearcoat: eggGlaze ? 0.3 : 0.04,
    clearcoatRoughness: 0.45,
    sheen: 0.5,
    sheenRoughness: 0.6,
    sheenColor: new THREE.Color(0xffd9a0),
  });
  const baked = { value: new THREE.Color(0x9a5522) }, pale = { value: new THREE.Color(0xf2d49c) };
  material.onBeforeCompile = (shader) => {
    shader.uniforms.baked = baked;
    shader.uniforms.pale = pale;
    shader.vertexShader = shader.vertexShader
      .replace(
        "#include <common>",
        `#include <common>\n        varying float vUp;`,
      )
      .replace(
        "#include <beginnormal_vertex>",
        `#include <beginnormal_vertex>\n        vUp = normalize(mat3(modelMatrix) * objectNormal).y;`,
      );
    shader.fragmentShader = shader.fragmentShader
      .replace(
        "#include <common>",
        `#include <common>\n        uniform vec3 baked;\n        uniform vec3 pale;\n        varying float vUp;`,
      )
      .replace(
        "#include <color_fragment>",
        `#include <color_fragment>\n        diffuseColor.rgb = mix(diffuseColor.rgb, baked, 0.6 * smoothstep(0.2, 1.0, vUp));\n        diffuseColor.rgb = mix(diffuseColor.rgb, pale, smoothstep(0.1, -0.7, vUp));`,
      );
  };
  return material;
}
